import { create } from 'zustand'
import { legacyContextService } from '@/services/legacy/LegacyContextService'
import { ApiError } from '@/services/ApiError'
import type {
  ContextDomain,
  ContextChunkSummary,
  ContextConflict,
  ConflictResolutionAction,
  GapAnalysisResult,
  ToolTemplate,
  FormattedContext,
  ContextChange,
} from '@/types/contextEngine'
import { DEV_GAP_ANALYSIS, DEV_CHUNKS, DEV_CONFLICTS, DEV_TEMPLATES, DEV_GENERATED_PACKAGE, DEV_HISTORY } from './devSeedData'

const USE_DEV_SEED = import.meta.env.VITE_USE_DEV_SEED === 'true'

interface ContextEngineState {
  gapAnalysis: GapAnalysisResult | null;
  gapAnalysisLoading: boolean;
  gapAnalysisError: string | null;

  chunks: Partial<Record<ContextDomain, ContextChunkSummary[]>>;
  chunksLoading: Partial<Record<ContextDomain, boolean>>;
  chunksError: string | null;

  conflicts: ContextConflict[];
  conflictsLoading: boolean;
  conflictsError: string | null;

  templates: ToolTemplate[];
  templatesLoading: boolean;
  templatesError: string | null;

  generatedPackage: FormattedContext | null;
  packageLoading: boolean;
  packageError: string | null;

  history: ContextChange[];
  historyLoading: boolean;
  historyError: string | null;
  historyHasMore: boolean;
  historyCursor: string | undefined;

  fetchGapAnalysis: () => Promise<void>;
  triggerGapAnalysis: () => Promise<void>;
  fetchChunks: (domain: ContextDomain) => Promise<void>;
  updateChunk: (
    chunkId: string,
    updates: { domain?: ContextDomain; confidence?: number; text?: string }
  ) => Promise<void>;
  archiveChunk: (chunkId: string, domain: ContextDomain) => Promise<void>;
  fetchConflicts: () => Promise<void>;
  resolveConflict: (
    conflictId: string,
    action: ConflictResolutionAction,
    mergedText?: string
  ) => Promise<void>;
  fetchTemplates: () => Promise<void>;
  generatePackage: (
    templateId: string,
    operation: string,
    options?: { userInstructions?: string; targetAudience?: string }
  ) => Promise<void>;
  clearPackage: () => void;
  fetchHistory: (domain?: ContextDomain, reset?: boolean) => Promise<void>;
  revertChange: (changeId: string) => Promise<void>;
  clearError: () => void;
}

export const useContextEngineStore = create<ContextEngineState>((set) => ({
  gapAnalysis: null,
  gapAnalysisLoading: false,
  gapAnalysisError: null,

  chunks: {},
  chunksLoading: {},
  chunksError: null,

  conflicts: [],
  conflictsLoading: false,
  conflictsError: null,

  templates: [],
  templatesLoading: false,
  templatesError: null,

  generatedPackage: null,
  packageLoading: false,
  packageError: null,

  history: [],
  historyLoading: false,
  historyError: null,
  historyHasMore: false,
  historyCursor: undefined,

  fetchGapAnalysis: async () => {
    set({ gapAnalysisLoading: true, gapAnalysisError: null })
    try {
      const result = await legacyContextService.getGapAnalysis()
      set({ gapAnalysis: result, gapAnalysisLoading: false })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load gap analysis'
      // Treat 404 as "no data yet" — not an error. Read it off ApiError.status:
      // sniffing the message for '404' silently stopped working the moment the
      // server sent a human-readable body ("No gap analysis found..."), and the
      // screen showed a hard error instead of an empty state.
      if (error instanceof ApiError && error.status === 404) {
        set({ gapAnalysis: null, gapAnalysisLoading: false })
      } else if (USE_DEV_SEED) {
        console.warn('[dev] API unavailable, using seed data for gap analysis')
        set({ gapAnalysis: DEV_GAP_ANALYSIS, gapAnalysisLoading: false })
      } else {
        set({ gapAnalysisError: message, gapAnalysisLoading: false })
      }
    }
  },

  triggerGapAnalysis: async () => {
    set({ gapAnalysisLoading: true, gapAnalysisError: null })
    try {
      const result = await legacyContextService.triggerGapAnalysis()
      set({ gapAnalysis: result, gapAnalysisLoading: false })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to trigger gap analysis'
      set({ gapAnalysisError: message, gapAnalysisLoading: false })
    }
  },

  fetchChunks: async (domain: ContextDomain) => {
    set((state) => ({
      chunksLoading: { ...state.chunksLoading, [domain]: true },
      chunksError: null,
    }))
    try {
      const result = await legacyContextService.listChunks(domain)
      set((state) => ({
        chunks: { ...state.chunks, [domain]: result.chunks },
        chunksLoading: { ...state.chunksLoading, [domain]: false },
      }))
    } catch (error) {
      if (USE_DEV_SEED) {
        console.warn(`[dev] API unavailable, using seed data for ${domain} chunks`)
        set((state) => ({
          chunks: { ...state.chunks, [domain]: DEV_CHUNKS[domain] ?? [] },
          chunksLoading: { ...state.chunksLoading, [domain]: false },
        }))
      } else {
        const message = error instanceof Error ? error.message : 'Failed to load chunks'
        set((state) => ({
          chunksError: message,
          chunksLoading: { ...state.chunksLoading, [domain]: false },
        }))
      }
    }
  },

  updateChunk: async (chunkId, updates) => {
    try {
      let updated: ContextChunkSummary
      if (USE_DEV_SEED) {
        // In dev mode, apply updates locally without hitting the API
        const state = useContextEngineStore.getState()
        const existing = Object.values(state.chunks).flat().find((c) => c?.chunkId === chunkId)
        if (!existing) throw new Error('Chunk not found')
        updated = { ...existing, ...updates, updatedAt: new Date().toISOString(), version: existing.version + 1 }
        console.warn('[dev] updateChunk applied locally (no API)')
      } else {
        updated = await legacyContextService.updateChunk(chunkId, updates)
      }
      set((state) => {
        const newDomain = updated.domain
        const newChunks = { ...state.chunks }

        // Remove from any old domain that still holds this chunk
        for (const [dom, arr] of Object.entries(newChunks)) {
          if (dom !== newDomain && arr?.some((c) => c.chunkId === chunkId)) {
            newChunks[dom as ContextDomain] = arr.filter((c) => c.chunkId !== chunkId)
          }
        }

        // Upsert in the new domain
        const domainChunks = newChunks[newDomain] ?? []
        const idx = domainChunks.findIndex((c) => c.chunkId === chunkId)
        if (idx >= 0) {
          newChunks[newDomain] = domainChunks.map((c) => (c.chunkId === chunkId ? updated : c))
        } else {
          newChunks[newDomain] = [...domainChunks, updated]
        }

        return { chunks: newChunks }
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update chunk'
      set({ chunksError: message })
    }
  },

  archiveChunk: async (chunkId, domain) => {
    try {
      if (!USE_DEV_SEED) {
        await legacyContextService.archiveChunk(chunkId)
      } else {
        console.warn('[dev] archiveChunk applied locally (no API)')
      }
      set((state) => {
        const domainChunks = state.chunks[domain] ?? []
        return {
          chunks: {
            ...state.chunks,
            [domain]: domainChunks.filter((c) => c.chunkId !== chunkId),
          },
        }
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to archive chunk'
      set({ chunksError: message })
    }
  },

  fetchConflicts: async () => {
    set({ conflictsLoading: true, conflictsError: null })
    try {
      const result = await legacyContextService.listConflicts()
      set({ conflicts: result.conflicts, conflictsLoading: false })
    } catch (error) {
      if (USE_DEV_SEED) {
        console.warn('[dev] API unavailable, using seed data for conflicts')
        set({ conflicts: DEV_CONFLICTS, conflictsLoading: false })
      } else {
        const message = error instanceof Error ? error.message : 'Failed to load conflicts'
        set({ conflictsError: message, conflictsLoading: false })
      }
    }
  },

  resolveConflict: async (conflictId, action, mergedText) => {
    try {
      if (USE_DEV_SEED) {
        console.warn('[dev] resolveConflict applied locally (no API)')
      } else {
        await legacyContextService.resolveConflict(conflictId, action, mergedText)
      }
      // Optimistic local update for immediate UI feedback
      set((state) => ({
        conflicts: state.conflicts.map((c) =>
          c.conflictId === conflictId
            ? { ...c, status: 'resolved' as const, resolution: action, resolvedAt: new Date().toISOString() }
            : c
        ),
      }))
      // Refetch from server to pick up side effects (merged chunks, resolved related conflicts)
      if (!USE_DEV_SEED) {
        const result = await legacyContextService.listConflicts()
        set({ conflicts: result.conflicts })
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to resolve conflict'
      set({ conflictsError: message })
    }
  },

  fetchTemplates: async () => {
    set({ templatesLoading: true, templatesError: null })
    try {
      const result = await legacyContextService.listTemplates()
      set({ templates: result.templates, templatesLoading: false })
    } catch (error) {
      if (USE_DEV_SEED) {
        console.warn('[dev] API unavailable, using seed data for templates')
        set({ templates: DEV_TEMPLATES, templatesLoading: false })
      } else {
        const message = error instanceof Error ? error.message : 'Failed to load templates'
        set({ templatesError: message, templatesLoading: false })
      }
    }
  },

  generatePackage: async (templateId, operation, options) => {
    set({ packageLoading: true, packageError: null })
    try {
      if (USE_DEV_SEED) {
        console.warn('[dev] generatePackage using seed data')
        // Simulate generation delay
        await new Promise((resolve) => setTimeout(resolve, 800))
        set({ generatedPackage: DEV_GENERATED_PACKAGE, packageLoading: false })
      } else {
        const result = await legacyContextService.generateContext(templateId, operation, options)
        set({ generatedPackage: result, packageLoading: false })
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to generate context package'
      set({ packageError: message, packageLoading: false })
    }
  },

  clearPackage: () => {
    set({ generatedPackage: null, packageError: null })
  },

  fetchHistory: async (domain, reset) => {
    const state = useContextEngineStore.getState()
    if (state.historyLoading && !reset) return // Guard against double-click
    const cursor = reset ? undefined : state.historyCursor
    set({ historyLoading: true, historyError: null })
    if (reset) set({ history: [], historyCursor: undefined })
    try {
      const result = await legacyContextService.listHistory(domain, 20, cursor)
      set((s) => ({
        history: reset ? result.changes : [...s.history, ...result.changes],
        historyHasMore: result.hasMore,
        historyCursor: result.cursor,
        historyLoading: false,
      }))
    } catch (error) {
      if (USE_DEV_SEED) {
        console.warn('[dev] API unavailable, using seed data for history')
        const filtered = domain
          ? DEV_HISTORY.filter((c) => c.domain === domain)
          : DEV_HISTORY
        set({ history: filtered, historyHasMore: false, historyLoading: false })
      } else {
        const message = error instanceof Error ? error.message : 'Failed to load history'
        set({ historyError: message, historyLoading: false })
      }
    }
  },

  revertChange: async (changeId) => {
    try {
      if (USE_DEV_SEED) {
        console.warn('[dev] revertChange applied locally (no API)')
      } else {
        await legacyContextService.revertChange(changeId)
      }
      // Refetch history to pick up the new "reverted" entry from server
      const { fetchHistory } = useContextEngineStore.getState()
      await fetchHistory(undefined, true)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to revert change'
      set({ historyError: message })
    }
  },

  clearError: () => {
    set({ gapAnalysisError: null, chunksError: null, conflictsError: null, templatesError: null, packageError: null, historyError: null })
  },
}))
